import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  buildRelationRowsDictionary,
  ComputedDataKey,
  errors,
  flattenExistingRelation,
  hasKeys,
  IExistingRelation,
  IMergedRow,
  IRydeProcess,
  PLURAL_KEY,
  RydeUiUnit,
  SINGLE_KEY,
  tableRowsAtomFamily,
} from "@ryde/shared";

export const useBulkArchiveRelationError = ({
  currentProcess,
  entity,
}: {
  currentProcess: IRydeProcess;
  entity: RydeUiUnit;
}) => {
  const [relationErrors, setRelationErrors] = useState<IMergedRow[]>([]);
  const [isPending, startTransition] = useTransition();
  const rows = useRecoilValue(tableRowsAtomFamily(entity));
  const { t } = useTranslation();

  const existingRelation = useMemo((): IExistingRelation => {
    let bulkArchiveErrorData = {};

    for (const error of currentProcess?.errors ?? []) {
      if (
        error.translationKey === errors.common.bulkArchiveRelationError &&
        error[ComputedDataKey]
      ) {
        bulkArchiveErrorData = {
          ...bulkArchiveErrorData,
          ...error[ComputedDataKey],
        };
      }
    }

    return bulkArchiveErrorData as IExistingRelation;
  }, [currentProcess?.errors]);

  const getMergedData = useCallback(
    (existingRelationData: IExistingRelation): IMergedRow[] => {
      const currentRows = rows ?? [];
      const rowsDictionary = buildRelationRowsDictionary(currentRows);
      return flattenExistingRelation(existingRelationData, rowsDictionary);
    },
    [rows],
  );

  const relationErrorsForCsv = useMemo(() => {
    return relationErrors.map(row => ({
      [t(`${entity}.${SINGLE_KEY}`)]: row.name || row.id,
      [t("general.RELATION_ENTITY")]: t(`${row.relation}.${PLURAL_KEY}`),
      [t("general.RELATION_IDS")]: row.ids.join(", "),
    }));
  }, [entity, relationErrors, t]);

  useEffect(() => {
    if (!existingRelation || !hasKeys(existingRelation)) {
      setRelationErrors([]);
      return;
    }

    startTransition(() => {
      setRelationErrors(getMergedData(existingRelation));
    });
  }, [existingRelation, getMergedData]);

  return {
    isPending,
    relationErrors,
    relationErrorsForCsv,
  };
};
